import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { useEffect, useRef } from "react";
import { saveReturnUrl } from "@/utils/stytchAuthHelpers";

interface RoleGuardProps {
  allowedRoles: ('candidate' | 'company' | 'admin')[];
  children: React.ReactNode;
  fallbackPath?: string;
}

export function RoleGuard({ allowedRoles, children, fallbackPath = "/" }: RoleGuardProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const hasAttemptedSaveRef = useRef(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      if (!allowedRoles.includes(user.role as 'candidate' | 'company' | 'admin')) {
        console.warn(`Access denied: User role '${user.role}' not in allowed roles:`, allowedRoles);
        setLocation(fallbackPath);
      }
    }
  }, [user, isAuthenticated, isLoading, allowedRoles, fallbackPath, setLocation]);

  // Still loading authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-primary"></div>
      </div>
    );
  }

  // Not authenticated - save current URL for post-login redirect (once only)
  if (!isAuthenticated) {
    if (typeof window !== 'undefined' && !hasAttemptedSaveRef.current) {
      const pathname = window.location.pathname;
      // Don't save auth-related URLs
      if (!pathname.startsWith('/auth') && !pathname.includes('/callback')) {
        const currentUrl = pathname + window.location.search;
        saveReturnUrl(currentUrl);
        hasAttemptedSaveRef.current = true;
      }
    }
    return null;
  }

  // Wrong role
  if (!user || !allowedRoles.includes(user.role as 'candidate' | 'company' | 'admin')) {
    return (
      <div className="min-h-screen bg-base-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-base-text mb-2">Access Denied</h2>
          <p className="text-base-text-muted">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Authorized
  return <>{children}</>;
}

// Convenience components for specific roles
export function CandidateOnly({ children, fallbackPath }: { children: React.ReactNode; fallbackPath?: string }) {
  return (
    <RoleGuard allowedRoles={['candidate']} fallbackPath={fallbackPath}>
      {children}
    </RoleGuard>
  );
}

export function CompanyOnly({ children, fallbackPath }: { children: React.ReactNode; fallbackPath?: string }) {
  return (
    <RoleGuard allowedRoles={['company']} fallbackPath={fallbackPath}>
      {children}
    </RoleGuard>
  );
}

export function AdminOnly({ children, fallbackPath = "/system/login" }: { children: React.ReactNode; fallbackPath?: string }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation(fallbackPath);
    }
  }, [isAuthenticated, isLoading, fallbackPath, setLocation]);

  if (!isAuthenticated && !isLoading) {
    return null;
  }

  return (
    <RoleGuard allowedRoles={['admin']} fallbackPath={fallbackPath}>
      {children}
    </RoleGuard>
  );
}

/**
 * CompanyAuthOnly - Requires company role authentication but NOT company profile
 * Used for /company-setup page where new B2B users create their initial profile
 */
export function CompanyAuthOnly({ children, fallbackPath = "/company-auth" }: { children: React.ReactNode; fallbackPath?: string }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        // Not authenticated - redirect to login
        setLocation(fallbackPath);
      } else if (user && user.role !== 'company') {
        // Wrong role - redirect to home
        setLocation('/');
      }
    }
  }, [user, isAuthenticated, isLoading, fallbackPath, setLocation]);

  // Still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-primary"></div>
      </div>
    );
  }

  // Not authenticated or wrong role
  if (!isAuthenticated || !user || user.role !== 'company') {
    return null;
  }

  // Authorized - company role with or without profile
  return <>{children}</>;
}